import React, { useState, useEffect, useContext } from 'react';
import TenantContext from '@/contexts/TenantContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket, 
  Clock, 
  CheckCircle, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Activity, 
  AlertCircle, 
  User, 
  FileText, 
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ShieldCheck,
  Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TicketsProPage = () => {
  const { tenantSlug } = useContext(TenantContext);
  const [tickets, setTickets] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sidebar State
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [formData, setFormData] = useState({
    folio: '',
    cliente: '',
    asunto: '',
    descripcion: '',
    prioridad: 'Media',
    estado: 'Pendiente'
  });

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/tickets`);
      setTickets(response.data || []);
    } catch (error) {
      if (error.response?.status === 404) {
         setTickets([]);
      } else {
         toast.error("Error al cargar tickets");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClientes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/clientes`);
      setClientes(response.data || []);
    } catch (error) {
      console.error("Error loading clients:", error);
    }
  };

  useEffect(() => {
    if (tenantSlug) {
      fetchTickets();
      fetchClientes();
    }
  }, [tenantSlug]);

  const resetForm = () => {
    setFormData({
      folio: `TIC-${Math.floor(1000 + Math.random() * 9000)}`,
      cliente: '',
      asunto: '',
      descripcion: '',
      prioridad: 'Media',
      estado: 'Pendiente'
    });
    setIsEditing(false);
    setSelectedTicketId(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsSidebarOpen(true);
  };

  const handleEdit = (ticket) => {
    setFormData({
      folio: ticket.folio || `TIC-${ticket._id.substring(ticket._id.length - 4)}`,
      cliente: ticket.cliente || '',
      asunto: ticket.asunto || '',
      descripcion: ticket.descripcion || ticket.notas || '',
      prioridad: ticket.prioridad || 'Media',
      estado: ticket.status || ticket.estado || 'Pendiente'
    });
    setSelectedTicketId(ticket._id);
    setIsEditing(true);
    setIsSidebarOpen(true);
  };

  const handleSave = async () => {
    if (!formData.cliente || !formData.asunto) {
      toast.error("Cliente y Asunto son obligatorios");
      return;
    }

    try {
      const dataToSave = { ...formData, status: formData.estado, estado: formData.estado };
      if (isEditing) {
        await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/tickets/${selectedTicketId}`, dataToSave);
        toast.success("Ticket actualizado");
      } else {
        await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/tickets/inject`, { data: dataToSave });
        toast.success("Ticket creado exitosamente");
      }
      setIsSidebarOpen(false);
      fetchTickets();
    } catch (error) {
      console.error("Save error details:", error.response?.data);
      toast.error("Error al guardar el ticket");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este ticket permanentemente?")) return;
    try {
      await axios.delete(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/tickets/${id}`);
      toast.success("Ticket eliminado");
      fetchTickets();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const updateStatusDirect = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/tickets/${id}`, { status: newStatus, estado: newStatus });
      toast.success(`Movido a ${newStatus}`);
      fetchTickets();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  // Logic for Kanban columns
  const getTicketStatus = (ticket) => {
      const s = (String(ticket.status || ticket.estado || 'pendiente')).toLowerCase();
      if (s.includes('resuelto') || s.includes('completado') || s.includes('done')) return 'resuelto';
      if (s.includes('proceso') || s.includes('progress') || s.includes('atendiendo')) return 'proceso';
      return 'pendiente';
  };

  const columns = [
    { id: 'pendiente', title: 'Pendientes', icon: Clock, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
    { id: 'proceso', title: 'En Proceso', icon: Activity, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
    { id: 'resuelto', title: 'Resueltos', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' }
  ];

  const filteredTickets = tickets.filter(t => 
    (t.asunto?.toLowerCase().includes(searchQuery.toLowerCase()) || 
     t.cliente?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     t.folio?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getPriorityStyle = (prio) => {
    const p = String(prio || '').toLowerCase();
    if (p === 'alta' || p === 'urgente') return 'bg-rose-500/20 text-rose-400 border-rose-500/30';
    if (p === 'media') return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  };

  return (
    <div className="space-y-8 pb-32">
      <div className="mb-8 relative">
        <div className="absolute inset-0 bg-blue-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="flex flex-col gap-6 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-blue-400" />
                <span className="text-[9px] uppercase tracking-[0.4em] font-black text-blue-500/60">Soporte Operativo</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit leading-none">
                Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Tickets</span>
              </h1>
              <p className="text-slate-400 mt-3 font-medium italic text-xs max-w-xl">Resuelve solicitudes de tus clientes mediante IA colaborativa.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 bg-white/[0.03] border border-white/5 p-2 rounded-[1.5rem] w-full lg:w-auto lg:min-w-[500px]"
            >
                <div className="flex items-center bg-slate-900/50 rounded-xl border border-white/5 px-2">
                  <button 
                    onClick={() => document.getElementById('tickets-board-container').scrollBy({ left: -300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="w-[1px] h-4 bg-white/5 mx-1" />
                  <button 
                    onClick={() => document.getElementById('tickets-board-container').scrollBy({ left: 300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="relative flex-1 group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    className="block w-full pl-9 pr-4 py-2.5 bg-slate-950/50 border border-white/10 rounded-xl text-xs placeholder-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-bold tracking-tight"
                    placeholder="Buscar ticket..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                  
                <button 
                  onClick={handleOpenNew}
                  className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg hover:-translate-y-0.5 active:scale-95 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nuevo</span>
                </button>
            </motion.div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Activity className="w-12 h-12 text-blue-500 animate-spin opacity-20" /></div>
      ) : (
        <div id="tickets-board-container" className="flex flex-col lg:flex-row gap-6 items-start overflow-x-auto pb-4">
          {columns.map(col => {
            const items = filteredTickets.filter(t => getTicketStatus(t) === col.id);
            return (
              <div key={col.id} className="flex-1 w-full flex flex-col min-h-[600px] bg-slate-900/40 rounded-[2.5rem] border border-white/5 p-6 backdrop-blur-xl">
                <div className={`p-5 rounded-[2rem] ${col.bg} ${col.border} border-2 border-dashed mb-8 flex items-center justify-between shadow-lg shadow-black/20`}>
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-2xl bg-white/10 ${col.color} shadow-inner`}>
                      <col.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className={`text-xs font-black uppercase tracking-[0.2em] ${col.color}`}>{col.title}</h3>
                      <p className="text-[9px] text-slate-500 font-bold uppercase mt-0.5 tracking-wider">Queue System</p>
                    </div>
                  </div>
                  <span className="text-xs font-black px-4 py-1.5 rounded-full bg-white/5 text-slate-300 border border-white/5">{items.length}</span>
                </div>

                <div className="flex-1 space-y-4">
                  <AnimatePresence>
                    {items.map(ticket => (
                      <motion.div
                        key={ticket._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onClick={() => handleEdit(ticket)}
                        className="group relative p-6 rounded-[2rem] bg-black/40 border border-white/5 hover:border-blue-500/30 transition-all hover:bg-black/60 hover:shadow-[0_20px_50px_rgba(0,0,0,0.5)] cursor-pointer"
                      >
                        <div className="flex flex-col h-full">
                          <div className="flex items-center justify-between mb-4">
                             <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2 py-1 rounded-lg border border-blue-500/20">{ticket.folio || `TIC-${ticket._id.substring(ticket._id.length - 4)}`}</span>
                                <span className={`text-[9px] font-black uppercase px-3 py-1 rounded-full border ${getPriorityStyle(ticket.prioridad)} shadow-sm`}>
                                  {ticket.prioridad || 'Normal'}
                                </span>
                             </div>
                             <div className={`w-2 h-2 rounded-full ${col.id === 'pendiente' ? 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.8)]' : col.id === 'proceso' ? 'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.8)]' : 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.8)]'}`} />
                          </div>

                          <h4 className="text-[15px] font-black text-white mb-2 leading-tight group-hover:text-blue-400 transition-colors tracking-tight italic uppercase">{ticket.asunto || 'Sin Asunto'}</h4>
                          
                          <div className="flex items-center gap-2 mb-6">
                             <div className="w-6 h-6 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                                <User className="w-3 h-3 text-blue-400" />
                             </div>
                             <p className="text-xs text-slate-300 font-bold uppercase tracking-tight">{ticket.cliente || 'Anónimo'}</p>
                          </div>

                          <div className="flex items-center justify-between mt-auto pt-5 border-t border-white/5">
                             <div className="flex flex-col">
                                <span className="text-[9px] text-slate-500 font-black uppercase tracking-widest mb-1">Iniciado</span>
                                <span className="text-[11px] text-slate-400 font-black">{new Date(ticket.engine_injected_at || Date.now()).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}</span>
                             </div>
                             
                             <div className="flex items-center gap-2">
                                {/* Quick Status Actions */}
                                <div className="flex items-center gap-1.5 mr-2 pr-2 border-r border-white/10">
                                   {col.id !== 'pendiente' && (
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); updateStatusDirect(ticket._id, 'Pendiente'); }}
                                        className="p-2 rounded-xl bg-rose-500/10 text-rose-400 hover:bg-rose-500 hover:text-white transition-all shadow-sm border border-rose-500/20" 
                                        title="Mover a Pendiente"
                                      >
                                         <Clock className="w-3.5 h-3.5" />
                                      </button>
                                   )}
                                   {col.id !== 'proceso' && (
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); updateStatusDirect(ticket._id, 'Proceso'); }}
                                        className="p-2 rounded-xl bg-amber-500/10 text-amber-400 hover:bg-amber-500 hover:text-white transition-all shadow-sm border border-amber-500/20" 
                                        title="Mover a Proceso"
                                      >
                                         <Activity className="w-3.5 h-3.5" />
                                      </button>
                                   )}
                                   {col.id !== 'resuelto' && (
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); updateStatusDirect(ticket._id, 'Resuelto'); }}
                                        className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-white transition-all shadow-sm border border-emerald-500/20" 
                                        title="Marcar Resuelto"
                                      >
                                         <CheckCircle className="w-3.5 h-3.5" />
                                      </button>
                                   )}
                                </div>

                                <div className="flex items-center gap-1.5">
                                   <button 
                                     onClick={(e) => { e.stopPropagation(); handleDelete(ticket._id); }}
                                     className="p-2 rounded-xl bg-white/5 text-slate-500 hover:text-rose-400 hover:bg-rose-500/20 transition-all border border-white/5"
                                   >
                                      <Trash2 className="w-4 h-4" />
                                   </button>
                                </div>
                             </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {items.length === 0 && (
                    <div className="py-12 flex flex-col items-center justify-center text-center opacity-20 border-2 border-dashed border-white/5 rounded-[2rem]">
                      <Ticket className="w-8 h-8 mb-2" />
                      <p className="text-[10px] font-black uppercase tracking-widest">Columna Vacía</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sidebar V5.0 */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md z-[100]"
              onClick={() => setIsSidebarOpen(false)}
            />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 220 }}
              className="fixed top-0 right-0 h-full w-full max-w-[500px] bg-[#0F172A] border-l border-white/10 z-[101] shadow-2xl flex flex-col overflow-hidden"
            >
              <div className="p-6 border-b border-white/5 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${isEditing ? 'bg-amber-500/10 border-amber-500/20' : 'bg-blue-500/10 border-blue-500/20'}`}>
                            <Ticket className={`w-5 h-5 ${isEditing ? 'text-amber-400' : 'text-blue-400'}`} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-white tracking-tighter uppercase italic">{isEditing ? 'Modificar Ticket' : 'Registrar Ticket'}</h2>
                            <p className="text-slate-500 text-[9px] font-black uppercase tracking-widest mt-0.5 italic">Soporte Técnico Fluxo</p>
                        </div>
                    </div>
                    <button onClick={() => setIsSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-500 transition-colors">
                        <Plus className="w-7 h-7 rotate-45" />
                    </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                  {/* Identification Group */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                       <div className="w-1 h-5 bg-blue-500 rounded-full" />
                       <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Identificación & Origen</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Folio / Referencia</label>
                        <input 
                            type="text" 
                            readOnly
                            className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-slate-400 font-black tracking-widest outline-none cursor-not-allowed italic text-xs"
                            value={formData.folio}
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-blue-400 ml-1 italic">Cliente Vinculado</label>
                        <div className="relative group">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 group-focus-within:text-blue-400 transition-colors" />
                            <input 
                                list="ventas-clientes-list"
                                type="text" 
                                className="w-full bg-blue-500/[0.02] border border-blue-500/20 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-blue-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                                value={formData.cliente}
                                onChange={e => setFormData({...formData, cliente: e.target.value})}
                                placeholder="Buscar en CRM..."
                            />
                            <datalist id="ventas-clientes-list">
                                {clientes.map(c => <option key={c._id} value={c.nombre} />)}
                            </datalist>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Details Group */}
                  <div className="space-y-6 pt-8 border-t border-white/5">
                    <div className="flex items-center gap-3">
                       <div className="w-1 h-5 bg-emerald-500 rounded-full" />
                       <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Detalles del Incidente</span>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-emerald-400 ml-1 italic">Asunto / Título *</label>
                        <div className="relative group">
                            <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500/50 group-focus-within:text-emerald-400 transition-colors" />
                            <input 
                                type="text" 
                                className="w-full bg-emerald-500/[0.02] border border-emerald-500/20 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-emerald-500/50 outline-none transition-all font-black placeholder:text-slate-800 italic text-xs"
                                value={formData.asunto}
                                onChange={e => setFormData({...formData, asunto: e.target.value})}
                                placeholder="Describa el motivo del ticket..."
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Descripción Extendida</label>
                        <textarea 
                            className="w-full bg-black/40 border border-white/5 rounded-xl p-3 text-white focus:border-blue-500/50 outline-none transition-all font-medium min-h-[100px] resize-none text-xs leading-relaxed italic"
                            value={formData.descripcion}
                            onChange={e => setFormData({...formData, descripcion: e.target.value})}
                            placeholder="Detalles adicionales..."
                        />
                    </div>
                  </div>

                  {/* Clasificación Group */}
                  <div className="space-y-8 pt-10 border-t border-white/5 pb-10">
                    <div className="flex items-center gap-3">
                       <div className="w-1.5 h-6 bg-amber-500 rounded-full" />
                       <span className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-400 italic">Clasificación & Estado</span>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Nivel de Prioridad</label>
                            <select 
                                className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-amber-500/50 outline-none transition-all font-black appearance-none uppercase tracking-widest text-xs"
                                value={formData.prioridad}
                                onChange={e => setFormData({...formData, prioridad: e.target.value})}
                            >
                                <option value="Baja">Baja</option>
                                <option value="Media">Media</option>
                                <option value="Alta">Alta</option>
                                <option value="Urgente">Urgente</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Estado de Atención</label>
                            <select 
                                className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-blue-500/50 outline-none transition-all font-black appearance-none uppercase tracking-widest text-xs"
                                value={formData.estado}
                                onChange={e => setFormData({...formData, estado: e.target.value})}
                            >
                                <option value="Pendiente">Pendiente</option>
                                <option value="Proceso">En Proceso</option>
                                <option value="Resuelto">Resuelto</option>
                            </select>
                        </div>
                    </div>
                  </div>
              </div>

              {/* Sidebar Footer */}
              <div className="p-6 border-t border-white/5 bg-white/[0.02] flex items-center gap-4">
                  <div className="flex-1 p-3 rounded-xl bg-blue-500/5 border border-blue-500/10 flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-blue-400" />
                      <div>
                          <p className="text-[8px] font-black text-blue-400 uppercase tracking-widest">Soporte Auditado</p>
                          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter">Fluxo Ops Engine</p>
                      </div>
                  </div>
                  <button 
                    onClick={handleSave}
                    className="flex-[2] py-3 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-xl shadow-[0_0_20px_rgba(37,99,235,0.2)] transition-all flex items-center justify-center gap-2 active:scale-95"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{isEditing ? 'Actualizar Registro' : 'Confirmar Registro'}</span>
                  </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TicketsProPage;

